/**
 * Day-boundary helpers for the admin report date filters.
 *
 * The report filters are date-only (`<input type="date">`, value "YYYY-MM-DD").
 * A bare day has to be widened into a real range before it can be sent as a
 * bound, otherwise picking From = To = today asks the API for the single
 * instant of midnight and matches nothing.
 *
 * Boundaries are built in the BROWSER's local timezone on purpose: the report
 * tables render every timestamp with toLocaleString(), so "23 July" in the
 * filter has to mean the same 23 July the admin is reading in the table. This
 * is also why the obvious `new Date('2026-07-23')` is wrong here — the spec
 * parses a date-only string as UTC midnight, which shifts the window by the
 * viewer's UTC offset (5h30m for the IST admins) and silently clips rows off
 * one end of the range while pulling in rows from the neighbouring day.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function atLocalTime(value, hours, minutes, seconds, ms) {
  const match = DATE_ONLY.exec(String(value ?? '').trim());
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const d = new Date(year, month, day, hours, minutes, seconds, ms);
  if (isNaN(d.getTime())) return null;
  // Reject out-of-range parts (e.g. "2026-13-45"): the Date constructor silently
  // rolls those over into a valid-but-wrong day rather than failing.
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d.toISOString();
}

/** Local 00:00:00.000 of `value` as an ISO string; null if not a valid date. */
export function startOfDayISO(value) {
  return atLocalTime(value, 0, 0, 0, 0);
}

/** Local 23:59:59.999 of `value` as an ISO string; null if not a valid date. */
export function endOfDayISO(value) {
  return atLocalTime(value, 23, 59, 59, 999);
}
